/**
 * Outfitters-style Cart Notification Popup
 * ------------------------------------------------------------------
 * - AJAX only, no page reload.
 * - Listens for PUB_SUB_EVENTS.cartUpdate, published by Dawn's
 *   product-form.js after every successful /cart/add.js request,
 *   regardless of cart_type (drawer / notification / page).
 * - Does not touch cart-drawer / cart-notification markup or logic.
 * - Also implements the minimal cart interface (getSectionsToRender,
 *   renderContents, setActiveElement) so it can act as a safe AJAX
 *   fallback for product-form.js when cart_type === "page" (no
 *   cart-drawer / cart-notification element exists on the page).
 */

(function () {
  if (customElements.get('cart-notification-popup')) return;

  const AUTO_CLOSE_MS = 5000;

  customElements.define(
    'cart-notification-popup',
    class CartNotificationPopup extends HTMLElement {
      constructor() {
        super();

        this.content = this.querySelector('#CartNotificationPopupContent');
        this.overlay = this.querySelector('[data-cnp-overlay]');
        this.closeButton = this.querySelector('[data-cnp-close]');
        this.continueButton = this.querySelector('[data-cnp-continue]');
        this.viewCartButton = this.querySelector('[data-cnp-view-cart]');
        this.timerBar = this.querySelector('[data-cnp-timer]');
        this.announcer = this.querySelector('[data-cnp-announcer]');

        this.imageEl = this.querySelector('[data-cnp-image]');
        this.productTitleEl = this.querySelector('[data-cnp-product-title]');
        this.variantTitleEl = this.querySelector('[data-cnp-variant-title]');
        this.sizeEl = this.querySelector('[data-cnp-size]');
        this.colorEl = this.querySelector('[data-cnp-color]');
        this.quantityEl = this.querySelector('[data-cnp-quantity]');
        this.priceEl = this.querySelector('[data-cnp-price]');

        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.autoCloseTimeout = null;
        this.activeElement = null;

        this.onKeyDown = this.onKeyDown.bind(this);
        this.close = this.close.bind(this);

        this.closeButton.addEventListener('click', this.close);
        this.continueButton.addEventListener('click', this.close);
        if (this.viewCartButton) {
          this.viewCartButton.addEventListener('click', () => {
            this.close();
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else {
              // fallback: navigate to cart page
              window.location.href = window.Shopify?.routes?.cart_url || '/cart';
            }
          });
        }
        this.overlay.addEventListener('click', this.close);
        this.addEventListener('keydown', this.onKeyDown);

        this.subscribeToCartUpdates();
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        this.clearAutoCloseTimer();
      }

      /* ------------------------------------------------------------ *
       * Cart update subscription (works with any cart_type)
       * ------------------------------------------------------------ */
      subscribeToCartUpdates() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event || !event.cartData) return;

          const item = event.cartData;

          // Only react to the line-item-shaped payload returned by
          // /cart/add.js (product_title is unique to that response).
          if (typeof item.product_title === 'undefined') return;

          this.showForItem(item);
        });
      }

      /* ------------------------------------------------------------ *
       * Public entry point — render + open for a given /cart/add.js
       * line-item response. Callable directly (bypassing pub/sub)
       * by any add-to-cart integration that wants a guaranteed toast,
       * regardless of whether other cartUpdate subscribers exist or
       * misbehave.
       * ------------------------------------------------------------ */
      showForItem(item) {
        this.renderItem(item);
        this.setActiveElement(document.activeElement);
        this.open();
      }

      /* ------------------------------------------------------------ *
       * Populate popup content from a /cart/add.js item response
       * ------------------------------------------------------------ */
      renderItem(item) {
        // Image
        const rawImage = (item.featured_image && item.featured_image.url) || item.image || '';
        if (rawImage) {
          this.imageEl.src = this.getResizedImageUrl(rawImage, 160);
        } else {
          this.imageEl.removeAttribute('src');
        }
        this.imageEl.alt = (item.featured_image && item.featured_image.alt) || item.product_title || '';

        // Title
        this.productTitleEl.textContent = item.product_title || '';

        // Break out Size / Color into their own rows when present; any other
        // option (e.g. Material) falls back onto the variant title line.
        const optionsWithValues = Array.isArray(item.options_with_values) ? item.options_with_values : [];
        const sizeOption = optionsWithValues.find((opt) => /^size$/i.test(opt.name));
        const colorOption = optionsWithValues.find((opt) => /^colou?r$/i.test(opt.name));
        const otherOptions = optionsWithValues.filter((opt) => opt !== sizeOption && opt !== colorOption);

        this.setOptionRow(this.sizeEl, 'Size', sizeOption ? sizeOption.value : '');
        this.setOptionRow(this.colorEl, 'Color', colorOption ? colorOption.value : '');

        // Variant title (hide for products with only the default variant,
        // and hide whatever portion is already shown via Size/Color rows).
        let variantTitle = '';
        if (optionsWithValues.length) {
          variantTitle = otherOptions.map((opt) => opt.value).join(' / ');
        } else if (item.variant_title && item.variant_title.toLowerCase() !== 'default title') {
          variantTitle = item.variant_title;
        }
        this.variantTitleEl.textContent = variantTitle;

        // Quantity
        const quantity = item.quantity || 1;
        this.quantityEl.textContent = `Qty: ${quantity}`;

        // Price (unit price)
        const price = typeof item.price === 'number' ? item.price : 0;
        this.priceEl.textContent = this.formatMoney(price, this.moneyFormat);

        // ARIA live announcement
        if (this.announcer) {
          const descriptors = [sizeOption && sizeOption.value, colorOption && colorOption.value, variantTitle]
            .filter(Boolean)
            .join(', ');
          const announcement = `${item.product_title}${descriptors ? ', ' + descriptors : ''} added to your cart`;
          this.announcer.textContent = '';
          // Re-trigger the live region after a tick so screen readers announce it
          requestAnimationFrame(() => {
            this.announcer.textContent = announcement;
          });
        }
      }

      setOptionRow(el, label, value) {
        if (!el) return;
        if (value) {
          el.textContent = `${label}: ${value}`;
          el.hidden = false;
        } else {
          el.textContent = '';
          el.hidden = true;
        }
      }

      getResizedImageUrl(url, width) {
        if (!url) return url;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}width=${width}`;
      }

      /* ------------------------------------------------------------ *
       * Money formatting (Shopify-standard money_format placeholders)
       * ------------------------------------------------------------ */
      formatMoney(cents, format) {
        if (typeof cents === 'string') cents = cents.replace('.', '');
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const formatString = format || '${{amount}}';

        const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
          if (isNaN(number) || number == null) return '0';
          number = (number / 100).toFixed(precision);

          const parts = number.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          const centsPart = parts[1] ? decimal + parts[1] : '';

          return dollars + centsPart;
        };

        const match = formatString.match(placeholderRegex);
        if (!match) return formatString;

        let value;
        switch (match[1]) {
          case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0);
            break;
          case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
          case 'amount':
          default:
            value = formatWithDelimiters(cents, 2);
            break;
        }

        return formatString.replace(placeholderRegex, value);
      }

      /* ------------------------------------------------------------ *
       * Open / close
       * ------------------------------------------------------------ */
      open() {
        this.clearAutoCloseTimer();

        this.classList.add('active');
        this.content.setAttribute('aria-hidden', 'false');

        // Restart the auto-close progress bar animation.
        if (this.timerBar) {
          this.timerBar.classList.remove('is-counting');
          // Force reflow so the CSS transition restarts cleanly.
          void this.timerBar.offsetHeight;
          requestAnimationFrame(() => this.timerBar.classList.add('is-counting'));
        }

        document.body.classList.add('cart-notification-popup-open');

        this.content.addEventListener(
          'transitionend',
          () => {
            this.content.focus();
            trapFocus(this.content, this.content);
          },
          { once: true }
        );

        this.autoCloseTimeout = setTimeout(() => this.close(), AUTO_CLOSE_MS);
      }

      close() {
        if (!this.classList.contains('active')) return;

        this.classList.remove('active');
        this.content.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('cart-notification-popup-open');

        if (this.timerBar) this.timerBar.classList.remove('is-counting');

        this.clearAutoCloseTimer();
        removeTrapFocus(this.activeElement);
      }

      clearAutoCloseTimer() {
        if (this.autoCloseTimeout) {
          clearTimeout(this.autoCloseTimeout);
          this.autoCloseTimeout = null;
        }
      }

      onKeyDown(event) {
        if (event.code === 'Escape') {
          event.preventDefault();
          this.close();
        }
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      /* ------------------------------------------------------------ *
       * Fallback "cart" interface for product-form.js.
       *
       * Only used when cart_type === "page" and neither cart-drawer
       * nor cart-notification exist on the page. In that scenario this
       * element becomes `this.cart` inside product-form.js, which keeps
       * /cart/add.js requests AJAX (no full page reload) and keeps the
       * header cart bubble count in sync. The "item added" toast itself
       * is always driven by the cartUpdate subscription above, so no
       * double-rendering occurs.
       * ------------------------------------------------------------ */
      getSectionsToRender() {
        return [{ id: 'cart-icon-bubble' }];
      }

      renderContents(parsedState) {
        if (!parsedState || !parsedState.sections) return;

        this.getSectionsToRender().forEach((section) => {
          const sectionId = section.id;
          const targetElement = document.getElementById(sectionId);
          const html = parsedState.sections[sectionId];

          if (!targetElement || !html) return;

          targetElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
        });
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return parsed ? parsed.innerHTML : '';
      }
    }
  );
})();
